/**
 * Receipt document model.
 *
 * The renderer-agnostic shape of a receipt. Thermal (ESC/POS), 80mm, A4 and PDF renderers
 * all consume this same model, so a change to GST breakup or the tax table can never drift
 * between a 58mm print and a PDF invoice.
 */

import type { Milli, Paise } from './money.js';
import { formatInr, formatQty } from './money.js';
import type { TaxBucket } from './tax.js';

export type PaperWidth = 58 | 80;

export interface ReceiptConfiguration {
  paperWidth: PaperWidth;
  /** characters per line — 32 for 58mm, 42/48 for 80mm */
  characterWidth: number;
  showLogo: boolean;
  showCustomerDetails: boolean;
  showGstDetails: boolean;
  showMrp: boolean;
  showHsnSummary: boolean;
  showTaxTable: boolean;
  showBarcode: boolean;
  showTerminal: boolean;
  showCashier: boolean;
  autoCut: boolean;
  openCashDrawer: boolean;
  copies: number;
  autoPrintAfterPayment: boolean;
  headerLines: string[];
  footerLines: string[];
  thankYouMessage: string;
}

export const DEFAULT_RECEIPT_CONFIGURATION: ReceiptConfiguration = {
  paperWidth: 80,
  characterWidth: 42,
  showLogo: false,
  showCustomerDetails: true,
  showGstDetails: true,
  showMrp: false,
  showHsnSummary: true,
  showTaxTable: true,
  showBarcode: true,
  showTerminal: true,
  showCashier: true,
  autoCut: true,
  openCashDrawer: false,
  copies: 1,
  autoPrintAfterPayment: true,
  headerLines: [],
  footerLines: [],
  thankYouMessage: 'Thank you for shopping with us!',
};

export interface ReceiptLine {
  name: string;
  /** The product's Tamil (or other local) shelf name, for a receipt printed in that language. */
  localName?: string;
  /** the unit price line, e.g. "40.00/KG x 1.425" */
  detail: string;
  qtyLabel: string;
  /** "B:BATCH-123" for a batch-tracked line, printed on the detail row so the name is never cut. */
  batchLabel?: string | null;
  mrpLabel: string | null;
  rateLabel: string;
  discountLabel: string;
  taxLabel: string;
  totalLabel: string;
  /**
   * Pre-formatted text at the width the document was BUILT for.
   *
   * Kept for callers that never see a `ReceiptPrinterProfile` (an ESC/POS encoder reading a
   * stored invoice, a PDF writer). `renderReceiptText` ignores it and re-fits to the profile
   * width, so one stored document can still print on both a 58mm and an 80mm till.
   */
  text: string;
}

export interface ReceiptPaymentLine {
  label: string;
  reference: string | null;
  amountLabel: string;
  text: string;
}

export interface ReceiptDocument {
  store: {
    name: string;
    address: string[];
    phone: string | null;
    email: string | null;
    gstin: string | null;
    stateCode: string | null;
  };
  invoice: {
    number: string;
    dateLabel: string;
    timeLabel: string;
    cashier: string | null;
    terminal: string | null;
    shift: string | null;
    /** distinguishes a reprint from the original print */
    isReprint: boolean;
    reprintReason: string | null;
  };
  customer: {
    name: string | null;
    phone: string | null;
    gstin: string | null;
    loyaltyId: string | null;
  } | null;
  lines: ReceiptLine[];
  totals: {
    grossLabel: string;
    discountLabel: string | null;
    taxableLabel: string;
    taxBreakupLabel: string;
    roundOffLabel: string;
    grandTotalLabel: string;
    itemCountLabel: string;
    /** the amount the customer pays after round-off, in words */
    amountInWords: string;
    /** "No Tax" bills only: what the bill came down by, e.g. "-41.00". */
    taxRemovalLabel?: string;
    /** "No Tax" recorded as exempt: every tax is zero and the receipt says so. */
    taxExempt?: boolean;
    /** On-account sale only: the receivable left on the customer account, never captured money. */
    onAccountLabel?: string;
    /** Due date of the on-account balance, as printed. */
    onAccountDueLabel?: string | null;
  };
  payments: ReceiptPaymentLine[];
  changeLabel: string | null;
  taxTable: { label: string; taxableLabel: string; cgstLabel: string; sgstLabel: string; igstLabel: string; cessLabel: string; totalLabel: string }[];
  hsnSummary: { hsn: string; description: string; taxableLabel: string; taxLabel: string; totalLabel: string }[];
  barcodeValue: string | null;
  footerLines: string[];
  thankYouMessage: string;
  characterWidth: number;
  paperWidth: PaperWidth;
  /** Set only for a Tamil or bilingual receipt: the till then draws it as HTML (Tamil does not fit fixed-width text). */
  language?: 'ta' | 'both';
}

const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function twoDigits(value: number): string {
  if (value < 0 || value > 99) return '';
  if (value < 20) return ONES[value] ?? '';
  const tens = TENS[Math.floor(value / 10)] ?? '';
  const ones = ONES[value % 10] ?? '';
  return ones ? `${tens}-${ones}` : tens;
}

function threeDigits(value: number): string {
  if (value <= 0) return '';
  if (value < 20) return ONES[value] ?? '';
  if (value < 100) {
    const tens = TENS[Math.floor(value / 10)] ?? '';
    const ones = ONES[value % 10] ?? '';
    return ones ? `${tens}-${ones}` : tens;
  }
  const hundreds = Math.floor(value / 100);
  const rest = value % 100;
  const head = `${ONES[hundreds]} Hundred`;
  return rest > 0 ? `${head} ${threeDigits(rest)}` : head;
}

/** Any 0-999 chunk, including the crore chunk on very large invoices. */
function chunkWords(value: number): string {
  if (value <= 0) return '';
  if (value < 100) return twoDigits(value);
  return threeDigits(value);
}

/** Indian numbering words: "Rupees One Thousand Two Hundred Thirty Four and Fifty Paise Only". */
export function amountInWords(paise: Paise): string {
  const negative = paise < 0;
  const absolute = Math.abs(paise);
  const rupees = Math.floor(absolute / 100);
  const paisePart = absolute % 100;

  const chunks: string[] = [];
  const crore = Math.floor(rupees / 1_00_00_000);
  const lakh = Math.floor((rupees % 1_00_00_000) / 1_00_000);
  const thousand = Math.floor((rupees % 1_00_000) / 1_000);
  const hundred = Math.floor((rupees % 1_000) / 100);
  const remainder = rupees % 100;

  if (crore > 0) chunks.push(`${chunkWords(crore)} Crore`);
  if (lakh > 0) chunks.push(`${twoDigits(lakh)} Lakh`);
  if (thousand > 0) chunks.push(`${twoDigits(thousand)} Thousand`);
  if (hundred > 0) chunks.push(`${ONES[hundred]} Hundred`);
  if (remainder > 0) chunks.push(twoDigits(remainder));

  // A total below a rupee would otherwise read "Rupees Zero and Five Paise Only", which
  // looks like a fault on the till. Say only the paise in that case.
  let words: string;
  if (rupees > 0) {
    words = `Rupees ${chunks.join(' ')}`;
    if (paisePart > 0) words += ` and ${twoDigits(paisePart)} Paise`;
  } else {
    words = paisePart > 0 ? `${twoDigits(paisePart)} Paise` : 'Rupees Zero';
  }
  words += ' Only';
  return negative ? `Minus ${words}` : words;
}

export interface BuildReceiptInput {
  configuration: ReceiptConfiguration;
  store: ReceiptDocument['store'];
  invoice: Omit<ReceiptDocument['invoice'], 'isReprint' | 'reprintReason'> & {
    isReprint?: boolean;
    reprintReason?: string | null;
  };
  customer?: ReceiptDocument['customer'];
  lines: {
    name: string;
    qtyMilli: Milli;
    unit: string;
    ratePaise: Paise;
    mrpPaise: Paise;
    discountPaise: Paise;
    taxPaise: Paise;
    totalPaise: Paise;
    batchNumber?: string | null;
    hsnCode?: string | null;
    localName?: string | null;
  }[];
  totals: {
    grossPaise: Paise;
    discountPaise: Paise;
    taxablePaise: Paise;
    gstPaise: Paise;
    cessPaise: Paise;
    roundOffPaise: Paise;
    grandTotalPaise: Paise;
  };
  payments: { label: string; amountPaise: Paise; reference: string | null }[];
  changePaise: Paise;
  taxBreakup: TaxBucket[];
  hsnSummary?: ReceiptDocument['hsnSummary'];
  /** Receipt language; absent or 'en' leaves the document exactly as before. */
  language?: 'en' | 'ta' | 'both';
  /** "No Tax" bills only. Absent on every other bill, whose receipt is unchanged. */
  taxRemoval?: { mode: string; removedPaise: Paise } | null;
  /** On-account sale only. Absent on immediate-payment bills, whose receipt is unchanged. */
  onAccount?: { amountPaise: Paise; dueDate: string | null } | null;
  // `dateLabel` and `timeLabel` live on `invoice`, not here. They used to be accepted at
  // the top level as well, where they were silently dropped - so a caller could set them
  // and print a receipt with a blank date. There is now exactly one way to pass them.
}

/** Pad/collapse a line to the paper's character width. */
export function fitToWidth(left: string, right: string, width: number): string {
  const rightText = right;
  const spaces = width - left.length - rightText.length;
  if (spaces >= 1) return `${left}${' '.repeat(spaces)}${rightText}`;
  if (rightText.length + 1 >= width) {
    // Give the right-hand value its own line rather than truncating the money.
    return `${left}\n${' '.repeat(Math.max(0, width - rightText.length))}${rightText}`;
  }
  return `${left.slice(0, Math.max(0, width - rightText.length - 1))} ${rightText}`;
}

export function buildReceipt(input: BuildReceiptInput): ReceiptDocument {
  const config = input.configuration;
  const width = config.characterWidth;

  const lines: ReceiptLine[] = input.lines.map((line) => {
    const qtyLabel = formatQty(line.qtyMilli, line.unit);
    const rateLabel = formatInr(line.ratePaise);
    const totalLabel = formatInr(line.totalPaise);
    const detail = `${rateLabel}/${line.unit} x ${qtyLabel}`;
    const discountLabel = line.discountPaise > 0 ? `-${formatInr(line.discountPaise)}` : '';
    const taxLabel = line.taxPaise > 0 ? formatInr(line.taxPaise) : '';
    const mrpLabel = config.showMrp && line.mrpPaise > 0 ? formatInr(line.mrpPaise) : null;
    const record: ReceiptLine = {
      name: line.name,
      detail,
      qtyLabel,
      batchLabel: line.batchNumber ? `B:${line.batchNumber}` : null,
      mrpLabel,
      rateLabel,
      discountLabel,
      taxLabel,
      totalLabel,
      text: '',
    };
    record.text = fitItemLine(record, width).join('\n');
    if (line.localName) record.localName = line.localName;
    return record;
  });

  const exempt = input.taxRemoval?.mode === 'exempt';
  const taxTable = config.showTaxTable && !exempt
    ? input.taxBreakup.map((bucket) => ({
        label: bucket.rateName,
        taxableLabel: formatInr(bucket.taxablePaise),
        cgstLabel: formatInr(bucket.cgstPaise),
        sgstLabel: formatInr(bucket.sgstPaise),
        igstLabel: formatInr(bucket.igstPaise),
        cessLabel: formatInr(bucket.cessPaise),
        // Use the stored bucket total so the receipt can never disagree with computeTotals.
        totalLabel: formatInr(bucket.totalPaise),
      }))
    : [];

  return {
    store: input.store,
    invoice: {
      ...input.invoice,
      isReprint: input.invoice.isReprint ?? false,
      reprintReason: input.invoice.reprintReason ?? null,
    },
    customer: config.showCustomerDetails ? (input.customer ?? null) : null,
    lines,
    totals: {
      grossLabel: formatInr(input.totals.grossPaise),
      discountLabel: input.totals.discountPaise > 0 ? `-${formatInr(input.totals.discountPaise)}` : null,
      taxableLabel: formatInr(input.totals.taxablePaise),
      taxBreakupLabel: formatInr(input.totals.gstPaise + input.totals.cessPaise),
      roundOffLabel:
        input.totals.roundOffPaise === 0
          ? formatInr(0)
          : `${input.totals.roundOffPaise > 0 ? '+' : '-'}${formatInr(Math.abs(input.totals.roundOffPaise), { symbol: false })}`,
      grandTotalLabel: formatInr(input.totals.grandTotalPaise),
      itemCountLabel: String(input.lines.length),
      amountInWords: amountInWords(input.totals.grandTotalPaise),
      ...(input.taxRemoval ? { taxRemovalLabel: `-${formatInr(Math.abs(input.taxRemoval.removedPaise))}` } : {}),
      ...(exempt ? { taxExempt: true } : {}),
      ...(input.onAccount && input.onAccount.amountPaise > 0 ? { onAccountLabel: formatInr(input.onAccount.amountPaise), onAccountDueLabel: input.onAccount.dueDate } : {}),
    },
    payments: input.payments.map((payment) => ({
      label: payment.label,
      reference: payment.reference,
      amountLabel: formatInr(payment.amountPaise),
      text: fitToWidth(payment.label, formatInr(payment.amountPaise), width),
    })),
    changeLabel: input.changePaise > 0 ? formatInr(input.changePaise) : null,
    taxTable,
    hsnSummary: config.showHsnSummary ? (input.hsnSummary ?? []) : [],
    barcodeValue: config.showBarcode ? input.invoice.number : null,
    footerLines: [...config.footerLines, config.thankYouMessage].filter(
      (line) => line !== undefined && line !== null && line !== '',
    ),
    thankYouMessage: config.thankYouMessage,
    characterWidth: width,
    paperWidth: config.paperWidth,
    ...(input.language && input.language !== 'en' ? { language: input.language } : {}),
  };
}

export interface ReceiptPrinterProfile {
  name: string;
  paperWidth: PaperWidth;
  characterWidth: number;
  autoCut: boolean;
  openCashDrawer: boolean;
  copies: number;
  /** driver mechanism, so a future Bluetooth/ESC-POS driver can be selected without a migration */
  driver: 'windows' | 'escpos' | 'network' | 'pdf';
  address?: string | null;
}

/**
 * Fold a composed detail string onto as many lines as the paper needs.
 *
 * The detail lines under an item and under a tax row are built by concatenation, so they
 * have no left/right split to protect and can outgrow a 32-column paper. On an 80mm till
 * this never fires; on a 58mm till it is the difference between a readable receipt and a
 * line of characters running off the edge of the paper.
 */
export function foldToWidth(text: string, width: number): string[] {
  if (width <= 0 || text.length <= width) return [text];

  const rows: string[] = [];
  let current = '';
  // Split on spaces so a token is never cut in half.
  for (const token of text.split(' ')) {
    if (token.length > width) {
      if (current) {
        rows.push(current);
        current = '';
      }
      for (let start = 0; start < token.length; start += width) {
        rows.push(token.slice(start, start + width));
      }
      continue;
    }
    const candidate = current ? `${current} ${token}` : token;
    if (candidate.length > width) {
      rows.push(current);
      current = token;
    } else {
      current = candidate;
    }
  }
  if (current) rows.push(current);
  return rows;
}

/**
 * Render one item line to a given paper width.
 *
 * Shared by `buildReceipt` (which caches the result at the configured width) and
 * `renderReceiptText` (which re-fits to the printer profile's width). A document built for
 * 80mm therefore still prints correctly on a 58mm till, instead of running ten characters
 * off the paper edge.
 */
function fitItemLine(line: ReceiptLine, width: number): string[] {
  const detail =
    `  ${line.detail}` +
    (line.batchLabel ? `  ${line.batchLabel}` : '') +
    (line.discountLabel ? `  ${line.discountLabel}` : '') +
    (line.taxLabel ? `  GST ${line.taxLabel}` : '') +
    (line.mrpLabel ? `  MRP ${line.mrpLabel}` : '');

  // A long name wraps onto more rows instead of being cut to make room for the amount; the
  // amount sits on the name's last row when it fits there, else on a row of its own.
  const nameRows = line.name.length + 1 + line.totalLabel.length <= width ? [line.name] : foldToWidth(line.name, width);
  const last = nameRows.pop() ?? '';
  const amountRows =
    last.length + 1 + line.totalLabel.length <= width
      ? [fitToWidth(last, line.totalLabel, width)]
      : [last, fitToWidth('', line.totalLabel, width)];
  return [...nameRows, ...amountRows, ...foldToWidth(detail, width)];
}

/** Plain-text 58mm/80mm rendering. Also the payload an ESC/POS driver would encode. */
export function renderReceiptText(document: ReceiptDocument, profile: ReceiptPrinterProfile): string {
  const width = profile.characterWidth;
  const centre = (text: string) => {
    const clipped = text.length > width ? text.slice(0, width) : text;
    const pad = Math.max(0, Math.floor((width - clipped.length) / 2));
    return `${' '.repeat(pad)}${clipped}`;
  };

  /**
   * Centre a line, wrapping on word boundaries rather than clipping.
   *
   * A hard clip turns "Thank you for shopping with us! Visit again" into
   * "Thank you for shopping with us! Visit agai" — which looks like a fault on the till and
   * reads badly on a customer's copy. Any word genuinely longer than the paper (a long
   * company name, a bare URL) still has to be cut, so the last resort stays a hard slice.
   */
  const centreWrapped = (text: string): string[] => {
    if (text.length <= width) return [centre(text)];

    const wrapped: string[] = [];
    let current = '';
    for (const word of text.split(/\s+/).filter(Boolean)) {
      if (word.length > width) {
        // A single word that cannot fit: flush what we have, then hard-slice it.
        if (current) {
          wrapped.push(centre(current));
          current = '';
        }
        for (let start = 0; start < word.length; start += width) {
          wrapped.push(centre(word.slice(start, start + width)));
        }
        continue;
      }
      const candidate = current ? `${current} ${word}` : word;
      if (candidate.length > width) {
        wrapped.push(centre(current));
        current = word;
      } else {
        current = candidate;
      }
    }
    if (current) wrapped.push(centre(current));
    return wrapped;
  };
  const rule = (char = '-') => char.repeat(width);

  const output: string[] = [];
  if (document.store.name) output.push(centre(document.store.name.toUpperCase()));
  for (const line of document.store.address) output.push(centre(line));
  if (document.store.phone) output.push(centre(`Ph: ${document.store.phone}`));
  // The seller GSTIN belongs on every GST invoice, including exempt-mode bills whose
  // tax table is empty by design. Gate on the receipt setting, not the table.
  if (document.store.gstin) {
    output.push(centre(`GSTIN: ${document.store.gstin}`));
  }
  output.push(rule('='));

  output.push(fitToWidth('Invoice', document.invoice.number, width));
  output.push(fitToWidth('Date', document.invoice.dateLabel, width));
  output.push(fitToWidth('Time', document.invoice.timeLabel, width));
  if (document.invoice.cashier) output.push(fitToWidth('Cashier', document.invoice.cashier, width));
  if (document.invoice.terminal) output.push(fitToWidth('Terminal', document.invoice.terminal, width));
  if (document.invoice.isReprint) {
    output.push(centre('*** REPRINT ***'));
    if (document.invoice.reprintReason) output.push(centre(document.invoice.reprintReason));
  }
  if (document.customer) {
    output.push(fitToWidth('Customer', document.customer.name ?? document.customer.phone ?? 'Walk-in', width));
    if (document.customer.phone) output.push(fitToWidth('Phone', document.customer.phone, width));
    if (document.customer.gstin) output.push(fitToWidth('Buyer GSTIN', document.customer.gstin, width));
  }

  output.push(rule());
  for (const line of document.lines) output.push(...fitItemLine(line, width));
  output.push(rule());

  output.push(fitToWidth('Gross', document.totals.grossLabel, width));
  if (document.totals.discountLabel) output.push(fitToWidth('Discount', document.totals.discountLabel, width));
  if (document.totals.taxRemovalLabel) output.push(fitToWidth(document.totals.taxExempt ? 'Tax removed' : '  incl. No Tax', document.totals.taxRemovalLabel, width));
  output.push(fitToWidth('Taxable', document.totals.taxableLabel, width));
  output.push(fitToWidth('GST', document.totals.taxBreakupLabel, width));
  if (document.totals.roundOffLabel !== formatInr(0)) {
    output.push(fitToWidth('Round Off', document.totals.roundOffLabel, width));
  }
  output.push(fitToWidth('TOTAL', document.totals.grandTotalLabel, width));
  // Amounts-in-words can exceed the paper width: wrap, never clip mid-word.
  output.push(...centreWrapped(document.totals.amountInWords));
  if (document.totals.taxExempt) output.push(centre('TAX EXEMPT BILL'));

  if (document.payments.length > 0) {
    output.push(rule());
    for (const payment of document.payments) {
      output.push(fitToWidth(payment.label, payment.amountLabel, width));
      if (payment.reference) output.push(fitToWidth('  Ref', payment.reference, width));
    }
  }
  if (document.changeLabel) output.push(fitToWidth('Change', document.changeLabel, width));
  // The receivable is printed apart from captured payments: it is money owed, not money taken.
  if (document.totals.onAccountLabel) {
    output.push(fitToWidth('On account', document.totals.onAccountLabel, width));
    if (document.totals.onAccountDueLabel) output.push(fitToWidth('  Due', document.totals.onAccountDueLabel, width));
  }

  if (document.taxTable.length > 0) {
    output.push(rule());
    output.push(centre('TAX BREAKUP'));
    for (const row of document.taxTable) {
      output.push(fitToWidth(row.label, row.totalLabel, width));
      const parts = [
        `Taxable ${row.taxableLabel}`,
        row.cgstLabel !== '₹0.00' ? `CGST ${row.cgstLabel}` : null,
        row.sgstLabel !== '₹0.00' ? `SGST ${row.sgstLabel}` : null,
        row.igstLabel !== '₹0.00' ? `IGST ${row.igstLabel}` : null,
        row.cessLabel !== '₹0.00' ? `Cess ${row.cessLabel}` : null,
      ].filter((part): part is string => part !== null);
      output.push(...foldToWidth(`  ${parts.join('  ')}`, width));
    }
  }

  if (document.hsnSummary.length > 0) {
    output.push(rule());
    output.push(centre('HSN SUMMARY'));
    for (const row of document.hsnSummary) {
      output.push(fitToWidth(`${row.hsn} ${row.description}`, row.totalLabel, width));
      output.push(...foldToWidth(`  Taxable ${row.taxableLabel}  Tax ${row.taxLabel}`, width));
    }
  }

  output.push(rule());
  // The footer and the thank-you line are prose, not data, so they wrap instead of clipping.
  for (const line of document.footerLines) output.push(...centreWrapped(line));
  if (document.barcodeValue) output.push(...centreWrapped(`[${document.barcodeValue}]`));

  return output.join('\n');
}
